import React, { useState, useRef, useEffect, ChangeEvent, KeyboardEvent } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'localization';
import { Button } from 'primereact/button';
import { InputTextarea } from 'primereact/inputtextarea';
import { Avatar } from 'primereact/avatar';
import { Message as PrimeMessage } from 'primereact/message';
import { Editor } from 'primereact/editor';
import { Card } from 'primereact/card';
import DOMPurify, { type Config as DOMPurifyConfig } from 'dompurify';
import { Skeleton } from 'primereact/skeleton';
import { useApiClientsOrNull } from '../../../context/ApiClientsContext';
import { useSphereInfo } from '../../../hooks/useSphereInfo';
import SphereAvatar from '../sphere-avatar/sphere-avatar';
import SpherePropertiesPanel from '../SpherePropertiesPanel/SpherePropertiesPanel';
import ThreadContext from '../../atoms/ThreadContext/ThreadContext';
import './SphereChatPanel.scss';
import { useConversation } from './UseConversation';
import MentionPopover, { MentionPopoverPick, SearchUserResult } from '../../atoms/Mention/MentionAtom';
import MentionChipsBar from '../../atoms/MentionChipsBar/MentionChipsBar';
// ⛔️ Removed: `import Quill from 'quill'`
import type { RootState } from '@center-pwa/store/store';
import type { GlyphColor } from '../../../types/glyph';

interface SphereChatPanelProps {
  contextKey: string; // expects "sphere:{sphereId}" or "sphere:{sphereId}|metaspace:{metaspaceId}"
  collapsible?: boolean; // optional prop to make the panel collapsible
  scrollToBottomOnLoad?: boolean; // optional prop to scroll to bottom on initial load (default: false)
  showThreadContext?: boolean; // optional prop to show/hide thread context (default: false)
  viewMode?: boolean; // when true, conversations are read-only (no add/edit/delete/reply)
  anchorMessageId?: string | null; // message ID to scroll to and highlight
  onAnchorComplete?: () => void; // called after scroll+highlight animation completes
}

type MentionSuggestion = { email: string; display: string; userId?: string; imageId?: string };

/* ──────────────────────────────────────────────────────────────────────────────
   Lazy Quill loader (avoid Jest ESM parse) + mention blot registration
──────────────────────────────────────────────────────────────────────────────── */
let QuillRef: typeof import('quill').default | null = null;

async function ensureQuill(): Promise<typeof import('quill').default | null> {
  if (QuillRef) return QuillRef;
  if (typeof globalThis === 'undefined') return null; // SSR/tests
  const mod = await import('quill'); // ESM import only in browser
  QuillRef = (mod as any).default || mod;
  return QuillRef;
}

async function ensureMentionBlot() {
  const Q = await ensureQuill();
  if (!Q) return;

  const key = 'formats/mention';
  const reg = Q.imports || {};
  if (reg[key]) return; // already registered

  const Embed: any = Q.import('blots/embed');

  class MentionBlot extends Embed {
    static readonly blotName = 'mention';
    static readonly tagName = 'SPAN';
    static readonly className = 'mention';
    static create(value: { email: string; display: string; userId?: string; imageId?: string }) {
      const node = super.create();
      node.dataset.mention = 'true';
      node.dataset.email = value.email;
      if (value.userId) node.dataset.userId = value.userId;
      if (value.imageId) node.dataset.imageId = value.imageId;
      node.textContent = `@${value.display}`;
      return node;
    }
    static value(node: HTMLElement) {
      return {
        email: node.dataset.email || '',
        userId: node.dataset.userId || undefined,
        imageId: node.dataset.imageId || undefined,
        display: (node.textContent || '')
          .replace(/[\u200B-\u200D\uFEFF]/g, '') // Remove zero-width and BOM characters
          .replace(/^@+/, '') // Remove leading @ symbols
          .trim(), // Remove whitespace
      };
    }
  }

  Q.register(MentionBlot);
}

/** Only renders the left side of the message header (avatar + name + time).
 *  Keeps the original .sphere-chat-message-header container intact.
 */
const AuthorBadge: React.FC<{
  authorSphereId: string;
  createdAt: string;
  edited?: boolean;
  actions?: React.ReactNode;
}> = ({ authorSphereId, createdAt, edited, actions }) => {
  const { t } = useTranslation();
  const { info, loading } = useSphereInfo(authorSphereId);

  const sphere = (info as any)?.sphere ?? (info as any);
  const props = sphere?.properties ?? {};

  const name = props.name || sphere?.label || authorSphereId || t('chatMessageCard.author.defaultName');

  const glyph = props.glyph as string | undefined;
  const glyphColor = props.glyphColor as GlyphColor | undefined;

  const formatDate = (dateString: string): string => {
    const messageDate = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - messageDate.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const messageDay = messageDate.toDateString();
    const todayDay = today.toDateString();
    const yesterdayDay = yesterday.toDateString();

    const timeString = messageDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

    if (messageDay === todayDay) {
      return t('chatMessageCard.time.today', { time: timeString });
    } else if (messageDay === yesterdayDay) {
      return t('chatMessageCard.time.yesterday', { time: timeString });
    } else if (diffDays <= 7) {
      const dayName = messageDate.toLocaleDateString([], { weekday: 'long' });
      return `${dayName} ${timeString}`;
    } else if (diffDays <= 365) {
      const monthDay = messageDate.toLocaleDateString([], { month: 'short', day: 'numeric' });
      return `${monthDay} ${timeString}`;
    } else {
      const fullDate = messageDate.toLocaleDateString([], { year: 'numeric', month: 'short', day: 'numeric' });
      return `${fullDate} ${timeString}`;
    }
  };

  return (
    <>
      <SphereAvatar
        glyph={glyph}
        glyphColor={glyphColor}
        sphereId={authorSphereId}
        sphereName={name}
        imageId={props.imageId}
        size="small"
        className="sphere-chat-author-avatar"
      />

      <div className="sphere-chat-message-meta">
        <span className="sphere-chat-message-author">{loading ? t('chatMessageCard.author.loading') : name}</span>
        <span className="sphere-chat-message-timestamp">{formatDate(createdAt)}</span>
        {edited && (
          <span className="sphere-chat-message-edited" style={{ color: 'var(--secondary-text-color)', fontSize: 11 }}>
            {t('chatMessageCard.edited')}
          </span>
        )}
        {actions}
      </div>
    </>
  );
};

const isEmail = (s: string) => /^[^\s@]+@[^\s@]{1,256}\.[^\s@]{2,}$/.test(s);

// Extracted SendButton component for reusability and performance
const SendButton: React.FC<{
  content: string;
  className?: string;
  disabled?: boolean;
  onSend: () => void;
  loading: boolean;
  canPost: boolean;
}> = ({ content, className = 'p-button-sm', disabled = false, onSend, loading, canPost }) => (
  <Button
    icon="pi pi-send"
    label="Send"
    className={className}
    onClick={onSend}
    loading={loading}
    disabled={!canPost || disabled || !content.trim() || content === '<p><br></p>'}
  />
);

const SkeletonMessage: React.FC = () => (
  <Card className="sphere-chat-card" style={{ background: 'var(--component-background-gradient)', color: 'inherit', borderRadius: '20px' }}>
    <div className="sphere-chat-message-header" style={{ alignItems: 'center' }}>
      <Skeleton shape="circle" size="2rem" className="mr-2" />
      <div style={{ flex: 1 }}>
        <Skeleton width="8rem" height="0.9rem" className="mb-2" />
        <Skeleton width="5rem" height="0.8rem" />
      </div>
      <div className="sphere-chat-message-actions" style={{ display: 'flex', gap: 6, marginLeft: 'auto' }}>
        <Skeleton width="2rem" height="2rem" />
        <Skeleton width="2rem" height="2rem" />
        <Skeleton width="2rem" height="2rem" />
      </div>
    </div>
    <div style={{ marginTop: 12, paddingLeft: 44, paddingRight: 10 }}>
      <Skeleton width="90%" className="mb-2" />
      <Skeleton width="80%" className="mb-2" />
      <Skeleton width="65%" />
    </div>
  </Card>
);

// Scroll helper function extracted from main component
const useScrollToBottom = (
  messages: any[],
  isExpanded: boolean,
  scrollToBottomOnLoad: boolean,
  bottomRef: React.RefObject<HTMLDivElement>,
  chatMainRef: React.RefObject<HTMLDivElement>,
  contextKey: string
) => {
  const prevMessageCountRef = useRef(0);
  const shouldAutoScrollRef = useRef(true);
  const initialScrollPendingRef = useRef(scrollToBottomOnLoad);
  const componentMountedRef = useRef(false);

  // Mark component as mounted
  useEffect(() => {
    componentMountedRef.current = true;
    return () => {
      componentMountedRef.current = false;
    };
  }, []);

  // Reset scroll pending flag when contextKey changes
  useEffect(() => {
    initialScrollPendingRef.current = scrollToBottomOnLoad;
  }, [contextKey, scrollToBottomOnLoad]);

  // Enhanced scroll logic with better timing handling
  useEffect(() => {
    const currentCount = messages.length;
    const prevCount = prevMessageCountRef.current;
    const shouldScrollForNewMessages = currentCount > prevCount && prevCount > 0;
    const shouldScrollForInitialLoad = prevCount === 0 && currentCount > 0 && (scrollToBottomOnLoad || initialScrollPendingRef.current);

    const performScroll = () => {
      if (bottomRef.current && chatMainRef.current && isExpanded) {
        const findScrollContainer = (element: HTMLElement): HTMLElement | null => {
          let current = element;
          while (current && current !== document.body) {
            const computedStyle = globalThis.getComputedStyle(current);
            const overflowY = computedStyle.overflowY;
            const overflowX = computedStyle.overflowX;
            const overflow = computedStyle.overflow;

            if (
              overflowY === 'auto' ||
              overflowY === 'scroll' ||
              overflowX === 'auto' ||
              overflowX === 'scroll' ||
              overflow === 'auto' ||
              overflow === 'scroll' ||
              current.scrollHeight > current.clientHeight ||
              current.classList.contains('p-scrollpanel-content') ||
              current.dataset.scrollable ||
              current.classList.contains('scrollable') ||
              current.classList.contains('panel-inner') ||
              current.classList.contains('metaspace-panel')
            ) {
              return current;
            }
            current = current.parentElement!;
          }
          return null;
        };

        const scrollContainer = findScrollContainer(chatMainRef.current) || chatMainRef.current;
        requestAnimationFrame(() => {
          if (bottomRef.current && componentMountedRef.current) {
            if (scrollContainer && 'scrollTop' in scrollContainer) {
              const maxScrollTop = scrollContainer.scrollHeight - scrollContainer.clientHeight;
              scrollContainer.scrollTop = maxScrollTop + 50;
            }

            bottomRef.current.scrollIntoView({
              behavior: 'smooth',
              block: 'end',
              inline: 'nearest',
            });

            setTimeout(() => {
              if (scrollContainer && 'scrollTop' in scrollContainer && componentMountedRef.current) {
                const maxScrollTop = scrollContainer.scrollHeight - scrollContainer.clientHeight;
                scrollContainer.scrollTop = maxScrollTop + 100;
              }
            }, 150);

            setTimeout(() => {
              if (scrollContainer && 'scrollTop' in scrollContainer && componentMountedRef.current) {
                const maxScrollTop = scrollContainer.scrollHeight - scrollContainer.clientHeight;
                scrollContainer.scrollTop = maxScrollTop + 100;
              }
            }, 500);
          }
        });
        initialScrollPendingRef.current = false;
      }
    };

    if ((shouldScrollForNewMessages || shouldScrollForInitialLoad) && shouldAutoScrollRef.current) {
      if (shouldScrollForInitialLoad && prevCount === 0) {
        setTimeout(performScroll, 100);
      } else {
        performScroll();
      }
    }

    prevMessageCountRef.current = currentCount;
  }, [messages, isExpanded, scrollToBottomOnLoad]);

  return { shouldAutoScrollRef, initialScrollPendingRef, componentMountedRef };
};

export const SphereChatPanel: React.FC<SphereChatPanelProps> = React.memo(
  ({ contextKey, collapsible = false, scrollToBottomOnLoad = false, viewMode = false, showThreadContext = false, anchorMessageId, onAnchorComplete }) => {
    console.log(`[SphereChatPanel] MOUNTING with contextKey: ${contextKey}`);

    // All hooks must be called before any conditional logic
    const { t } = useTranslation();
    const { messages, perms, err, sendMessage, editMessage, deleteMessage, isDeleting, loading } = useConversation(contextKey, viewMode);
    const canParticipateInProps = Boolean(perms?.canEdit || perms?.canPost);
    const effectiveCanPost = Boolean(perms?.canPost && !viewMode);
    const effectiveCanEdit = Boolean(perms?.canEdit && !viewMode);
    const [composer, setComposer] = useState<string>('');
    const [replyDrafts, setReplyDrafts] = useState<Record<string, string>>({});
    const [isExpanded, setIsExpanded] = useState<boolean>(true); // Start expanded by default
    const [sending, setSending] = useState<boolean>(false);
    const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
    const [editDraft, setEditDraft] = useState<string>('');
    const bottomRef = useRef<HTMLDivElement>(null);
    const [showRich, setShowRich] = useState(false);
    const [replyTo, setReplyTo] = useState<string | null>(null);
    const apiClients = useApiClientsOrNull();

    // Detect mobile to disable rich editor on mobile devices
    const [isMobile, setIsMobile] = useState(() => {
      if (globalThis.window !== undefined) {
        return globalThis.window.matchMedia('(max-width: 768px)').matches;
      }
      return false;
    });

    useEffect(() => {
      if (globalThis.window === undefined) return;

      const mediaQuery = globalThis.window.matchMedia('(max-width: 768px)');

      const checkMobile = () => {
        setIsMobile(mediaQuery.matches);
      };

      checkMobile();

      // Modern browsers support addEventListener
      mediaQuery.addEventListener('change', checkMobile);
      return () => mediaQuery.removeEventListener('change', checkMobile);
    }, []);

    const editorWrapperRef = useRef<HTMLDivElement>(null);
    const editEditorRef = useRef<Editor | null>(null);
    const editWrapperRef = useRef<HTMLDivElement | null>(null);
    const replyEditorRefs = useRef<Record<string, Editor | null>>({});
    const replyWrapperRefs = useRef<Record<string, HTMLDivElement | null>>({});
    const chatMainRef = useRef<HTMLDivElement>(null);

    // Mobile menu state
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [selectedMessageForMenu, setSelectedMessageForMenu] = useState<string | null>(null);
    const [longPressTimer, setLongPressTimer] = useState<NodeJS.Timeout | null>(null);

    // Use extracted scroll hook
    const { shouldAutoScrollRef } = useScrollToBottom(
      messages,
      isExpanded,
      scrollToBottomOnLoad,
      bottomRef,
      chatMainRef,
      contextKey
    );

    // Scroll-to-message anchoring effect
    useEffect(() => {
      if (!anchorMessageId || !messages.length) return;

      let cleanupTimeout: ReturnType<typeof setTimeout> | undefined;

      const scrollTimeout = setTimeout(() => {
        const container = chatMainRef.current;
        if (!container) return;

        const target = container.querySelector(
          `[data-message-id="${CSS.escape(anchorMessageId)}"]`
        );
        if (!target) return;

        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        target.classList.add('sphere-chat-message--anchored');

        cleanupTimeout = setTimeout(() => {
          target.classList.remove('sphere-chat-message--anchored');
          onAnchorComplete?.();
        }, 2000);
      }, 300);

      return () => {
        clearTimeout(scrollTimeout);
        if (cleanupTimeout) clearTimeout(cleanupTimeout);
      };
    }, [anchorMessageId, messages.length, onAnchorComplete]);

    // Early return after all hooks have been called
    if (!apiClients) {
      return null;
    }

    const { graphClient } = apiClients;



    // Function to add border-radius to deepest cards and fix background colors
    function colorDeepestCards() {
      let globalCounter = 0;

      // Get all message containers in DOM order
      const allContainers = document.querySelectorAll<HTMLElement>('.sphere-chat-message-container');

      // First pass: Apply correct backgrounds to all messages in order
      for (const container of Array.from(allContainers)) {
        const card = container.querySelector(':scope > .sphere-chat-card') as HTMLElement;
        if (card && !card.classList.contains('sphere-chat-draft-reply')) {
          const levelClass = Array.from(container.classList).find((c) => c.startsWith('level-'));
          const level = levelClass && typeof levelClass === 'string' ? Number.parseInt(levelClass.slice(6), 10) : 0;
          const isEven = globalCounter % 2 === 0;
          const hasReplies = container.querySelector('.sphere-chat-replies-inline') !== null;

          // Apply background and shadow
          card.style.setProperty('background', getBgColor(level, isEven), 'important');
          card.style.setProperty('box-shadow', getBoxShadow(level, isEven, hasReplies), 'important');

          // Reset border-radius
          card.style.removeProperty('border-radius');

          globalCounter++;
        }
      }

      // Second pass: Apply border-radius to deepest cards in each thread
      const rootContainers = document.querySelectorAll<HTMLElement>('.sphere-chat-message-container.level-0');
      for (const root of Array.from(rootContainers)) {
        const hasReplies = root.querySelector('.sphere-chat-replies-inline');
        if (!hasReplies) continue;

        const all = root.querySelectorAll<HTMLElement>('.sphere-chat-message-container');
        let deepest: HTMLElement = root;
        let maxLevel = 0;

        for (const el of Array.from(all)) {
          const levelClass = Array.from(el.classList).find((c) => c.startsWith('level-'));
          const n = levelClass && typeof levelClass === 'string' ? Number.parseInt(levelClass.slice(6), 10) : 0;
          if (n >= maxLevel) {
            maxLevel = n;
            deepest = el;
          }
        }

        deepest.querySelector<HTMLElement>(':scope > .sphere-chat-card')?.style.setProperty('border-radius', '0px 0px 20px 20px', 'important');
      }
    }

    // Apply border-radius to deepest cards after messages change
    useEffect(() => {
      colorDeepestCards();
    }, [messages]);

    // Disable auto-scroll when replying, re-enable when not replying
    useEffect(() => {
      shouldAutoScrollRef.current = !replyTo;
    }, [replyTo]);

    // Load Quill & register mention blot once in the browser
    useEffect(() => {
      ensureMentionBlot();
    }, []);

    // Auto-focus Editor when switching from InputTextarea to rich editor
    useEffect(() => {
      if (showRich && composerRef.current) {
        // Use setTimeout to ensure the Editor is fully rendered
        setTimeout(() => {
          const quill = composerRef.current?.getQuill();
          if (quill) {
            quill.focus();
            // Move cursor to end of content
            const length = quill.getLength();
            quill.setSelection(length - 1, 0);
          }
        }, 100);
      }
    }, [showRich]);

    // Sanitize
    const PURIFY_CONFIG: DOMPurifyConfig = {
      ALLOWED_TAGS: ['a', 'b', 'strong', 'i', 'em', 'u', 's', 'p', 'br', 'ul', 'ol', 'li', 'pre', 'code', 'blockquote', 'span', 'img'],
      ALLOWED_ATTR: [
        'href',
        'target',
        'rel',
        'style',
        'src',
        'alt',
        'title',
        'width',
        'height',
        // keep mention styling/data
        'class',
        'data-mention',
        'data-email',
        'data-user-id',
        'data-image-id',
      ],
      ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto):|data:image\/(?:png|jpeg|gif|webp|svg\+xml);base64,)/i,
    };

    let hooksAdded = false;
    function addStyleWhitelistHook() {
      if (hooksAdded) return;
      hooksAdded = true;

      DOMPurify.addHook('uponSanitizeAttribute', (_node, data) => {
        if (data.attrName !== 'style') return;
        const allowed = new Set(['color', 'background-color', 'text-align', 'font-weight', 'font-style', 'text-decoration']);
        const safe = data.attrValue
          .split(';')
          .map((r) => r.trim())
          .filter(Boolean)
          .filter((rule) => {
            const prop = rule.split(':')[0]?.trim().toLowerCase();
            return allowed.has(prop);
          })
          .join('; ');
        data.attrValue = safe;
      });
    }

    const sanitize = (html: string) => {
      addStyleWhitelistHook();
      return DOMPurify.sanitize(html, PURIFY_CONFIG);
    };

    // ──────────────────────────────────────────────────────────────────────────────
    // Send / Edit
    const handleSend = async () => {
      if (!effectiveCanPost) return;

      const text = replyTo ? replyDrafts[replyTo] ?? '' : composer;
      if (!text.trim() || text === '<p><br></p>') return;

      // Extract mention user IDs to send to backend
      const mentionedUserIds = replyTo
        ? (replyMentions[replyTo] || []).map(m => m.userId).filter((id): id is string => !!id)
        : selectedMentions.map(m => m.userId).filter((id): id is string => !!id);

      setSending(true);
      try {
        await sendMessage(sanitize(text), replyTo ?? undefined, mentionedUserIds);

        if (replyTo) {
          setReplyDrafts((d) => {
            const next = { ...d };
            delete next[replyTo];
            return next;
          });
          // Clean up mentions and editor ref
          setReplyMentions((prev) => {
            const next = { ...prev };
            delete next[replyTo];
            return next;
          });
          delete replyEditorRefs.current[replyTo];
          delete replyWrapperRefs.current[replyTo];
          setReplyTo(null);
        } else {
          setComposer('');
          setShowRich(false);
          setSelectedMentions([]); // clear chips bar after send
        }
      } catch (e) {
        console.error('send failed', e);
      } finally {
        setSending(false);
      }
    };

    const handleEditSave = async (messageId: string) => {
      if (!effectiveCanEdit) return;
      const trimmed = editDraft.trim();
      if (!trimmed || trimmed === '<p><br></p>') return;
      try {
        await editMessage(messageId, sanitize(trimmed));
        setEditingMessageId(null);
        setEditDraft('');
      } catch (e: any) {
        console.error('edit failed', e);
      }
    };

    // ──────────────────────────────────────────────────────────────────────────────
    // Theme
    const theme = document.documentElement.getAttribute('data-theme') === 'dark' ? 'dark' : 'light';
    const getBgColor = (level: number, isEven: boolean) => {
      if (level === 0) return 'var(--msg-root-bg)';
      return isEven ? 'var(--msg-reply-even-bg)' : 'var(--msg-reply-odd-bg)';
    };

    // Shadow constants
    const SHADOW_STYLES = {
      ROOT_INSET: '5px 4px 4px 0 rgba(0, 0, 0, 0.50) inset',
      ROOT_WITH_BOTTOM: '5px 4px 4px 0 rgba(0, 0, 0, 0.50) inset, 0 4px 1px 0 rgba(0, 0, 0, 0.25)',
      REPLY_INSET: '5px 4px 4px 0 rgba(0, 0, 0, 0.35) inset',
      REPLY_WITH_BOTTOM: '5px 4px 4px 0 rgba(0, 0, 0, 0.35) inset, 0 4px 1px 0 rgba(0, 0, 0, 0.25)',
    };

    const getBoxShadow = (level: number, isEven: boolean, hasReplies: boolean) => {
      if (level === 0) {
        // Root message shadow
        return hasReplies ? SHADOW_STYLES.ROOT_INSET : SHADOW_STYLES.ROOT_WITH_BOTTOM;
      } else {
        // Reply message shadow
        return hasReplies ? SHADOW_STYLES.REPLY_INSET : SHADOW_STYLES.REPLY_WITH_BOTTOM;
      }
    };

    // Common UI constants
    const UI_CONSTANTS = {
      TOOLTIP_OPTIONS: { showDelay: 800, hideDelay: 0, position: 'top' as const },
      ACTION_BUTTON_CLASS: 'p-button-text p-button-sm',
      EDITOR_WRAPPER_STYLE: { marginLeft: 8, marginTop: 8, position: 'relative' as const, overflow: 'visible' as const },
      REPLY_EDITOR_STYLE: { position: 'relative' as const, overflow: 'visible' as const, padding: '10px' },
      MAIN_EDITOR_STYLE: { flex: 1, position: 'relative' as const, overflow: 'visible' as const, marginRight: '0px' },
    };

    // Common event handler utility
    const stopEventPropagation = (e: React.MouseEvent, callback: () => void) => {
      e.preventDefault();
      e.stopPropagation();
      callback();
    };

    // Reusable hover handlers
    const getMessageHoverProps = (messageId: string) => ({
      onMouseEnter: () => setHoveredMessageId(messageId),
      onMouseLeave: () => setHoveredMessageId(null),
    });

    // Reusable action button component
    const MessageActionButton: React.FC<{
      icon: string;
      tooltip: string;
      ariaLabel: string;
      onClick: (e: React.MouseEvent) => void;
      disabled?: boolean;
      type?: 'button';
    }> = ({ icon, tooltip, ariaLabel, onClick, disabled = false, type }) => (
      <Button
        icon={icon}
        className={UI_CONSTANTS.ACTION_BUTTON_CLASS}
        tooltip={tooltip}
        tooltipOptions={UI_CONSTANTS.TOOLTIP_OPTIONS}
        onClick={onClick}
        aria-label={ariaLabel}
        disabled={disabled}
        type={type}
      />
    );

    // Helper function to get the appropriate quill editor
    const getQuillEditor = (isEdit?: boolean, messageId?: string) => {
      if (isEdit) {
        return editEditorRef.current?.getQuill();
      } else if (messageId) {
        return replyEditorRefs.current[messageId]?.getQuill();
      } else {
        return composerRef.current?.getQuill();
      }
    };

    // Helper function to update content after mention insertion
    const updateContentAfterMention = (htmlContent: string, isEdit?: boolean, messageId?: string) => {
      if (isEdit) {
        setEditDraft(htmlContent);
        recomputeEditMentions(htmlContent);
      } else if (messageId) {
        setReplyDrafts((d) => ({ ...d, [messageId]: htmlContent }));
        recomputeReplyMentions(messageId, htmlContent);
      } else {
        setComposer(htmlContent);
        recomputeSelectedMentions(htmlContent);
      }
    };

    // Helper function to insert mention embed or HTML
    const insertMentionContent = (quill: any, tok: any, value: any, display: string) => {
      quill.deleteText(tok.start, tok.tokenLen, 'user');

      const hasMentionBlot = !!QuillRef?.imports?.['formats/mention'];
      if (hasMentionBlot) {
        quill.insertEmbed(tok.start, 'mention', value, 'user');
        quill.insertText(tok.start + 1, ' ', 'user');
        quill.setSelection(tok.start + 2, 0, 'user');
      } else {
        const safeDisplay = DOMPurify.sanitize(display, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });
        const html =
          `<span class="mention" data-mention="true"` +
          (value.email ? ` data-email="${value.email}"` : '') +
          (value.userId ? ` data-user-id="${value.userId}"` : '') +
          (value.imageId ? ` data-image-id="${value.imageId}"` : '') +
          `>@${safeDisplay}</span>&nbsp;`;
        quill.clipboard.dangerouslyPasteHTML(tok.start, html, 'user');
        quill.setSelection(tok.start + `@${safeDisplay}`.length + 1, 0, 'user');
      }
    };

    // Message action handlers to reduce nesting
    const handleReplyAction = (messageId: string) => () => {
      if (effectiveCanPost) {
        handleReplyClick(messageId);
      }
    };

    const handleEditAction = (messageId: string, content: string) => () => {
      setEditingMessageId(messageId);
      setEditDraft(content);
      recomputeEditMentions(content);
    };

    // Mobile menu handlers
    const handleOpenMobileMenu = (messageId: string) => {
      setSelectedMessageForMenu(messageId);
      setMobileMenuOpen(true);
    };

    const handleCloseMobileMenu = () => {
      setMobileMenuOpen(false);
      setTimeout(() => setSelectedMessageForMenu(null), 300); // Clear after animation
    };

    const handleBackdropKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handleCloseMobileMenu();
      }
      if (event.key === 'Escape') {
        handleCloseMobileMenu();
      }
    };

    const handleMobileMenuAction = (action: 'reply' | 'edit' | 'delete', messageId: string, content?: string) => {
      handleCloseMobileMenu();

      switch (action) {
        case 'reply':
          if (effectiveCanPost) {
            handleReplyClick(messageId);
          }
          break;
        case 'edit':
          if (content) {
            setEditingMessageId(messageId);
            setEditDraft(content);
            recomputeEditMentions(content);
          }
          break;
        case 'delete':
          // Delete will be handled by the existing delete handler
          break;
      }
    };

    // Long-press handlers for mobile
    const handleTouchStart = (messageId: string) => {
      const timer = setTimeout(() => {
        handleOpenMobileMenu(messageId);
      }, 500); // 500ms long press
      setLongPressTimer(timer);
    };

    const handleTouchEnd = () => {
      if (longPressTimer) {
        clearTimeout(longPressTimer);
        setLongPressTimer(null);
      }
    };

    // Helper for mention setup to reduce nesting
    const setupMentionForEditor = (quill: any, messageId?: string, isEdit?: boolean) => {
      if (quill) {
        setTimeout(() => openMention(quill, messageId, isEdit), 0);
      }
    };

    // Helper to handle reply cancellation
    const handleCancelReply = (messageId: string) => () => {
      setReplyTo(null);
      setReplyDrafts((d) => {
        const next = { ...d };
        delete next[messageId];
        return next;
      });
      // Clean up mentions and editor refs
      setReplyMentions((prev) => {
        const next = { ...prev };
        delete next[messageId];
        return next;
      });
      delete replyEditorRefs.current[messageId];
      delete replyWrapperRefs.current[messageId];
    };

    // Helper function to get placeholder text
    const getInputPlaceholder = () => {
      if (effectiveCanPost) return 'Type your message...';
      if (viewMode) return 'Read-only mode';
      return 'You do not have permission to post';
    };

    // ──────────────────────────────────────────────────────────────────────────────
    // Threaded view
    const childrenMap = React.useMemo(() => {
      const map: Record<string, any[]> = {};
      for (const m of messages) {
        const key = m.parent_message_id ?? '__root__';
        if (!map[key]) map[key] = [];
        map[key].push(m);
      }
      // Sort each list by created_at ascending
      Object.keys(map).forEach((key) => {
        map[key] = map[key].slice().sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
      });
      return map;
    }, [messages]);

    const renderLoadingSkeletons = (count = 3) => (
      <>
        {Array.from({ length: count }, (_, i) => (
          <div key={`skeleton-${i}`} className="sphere-chat-message-container">
            <SkeletonMessage />
          </div>
        ))}
      </>
    );

    // ──────────────────────────────────────────────────────────────────────────────
    // Mentions (lookup + popover + chips bar)

    // PrimeReact Editor ref
    const composerRef = useRef<Editor>(null);

    // popover/search state
    const [mentionOpen, setMentionOpen] = useState(false);
    const [mentionAnchor, setMentionAnchor] = useState<{ left: number; top: number } | null>(null);
    const [mentionQuery, setMentionQuery] = useState<string>('');
    const [mentionResults, setMentionResults] = useState<SearchUserResult[]>([]);
    const [mentionLoading, setMentionLoading] = useState<boolean>(false);
    const [activeIndex, setActiveIndex] = useState<number>(0);
    const lookupTimer = useRef<number | null>(null);
    const lookupSeq = useRef(0);

    const isValidEmail = isEmail;
    const MIN_CHARS = 1; // don’t search when query length < 1

    // chips state
    const [selectedMentions, setSelectedMentions] = useState<MentionSuggestion[]>([]);
    const [replyMentions, setReplyMentions] = useState<Record<string, MentionSuggestion[]>>({});
    const [editMentions, setEditMentions] = useState<MentionSuggestion[]>([]);
    const [activeEditorMessageId, setActiveEditorMessageId] = useState<string | null>(null);

    const dedupeMentions = (arr: MentionSuggestion[]) => {
      const map = new Map(arr.map((m) => [m.userId || m.email.toLowerCase(), m]));
      return Array.from(map.values());
    };

    const parseMentionsFromHtml = (html: string): MentionSuggestion[] => {
      const holder = document.createElement('div');
      holder.innerHTML = html;
      const out: MentionSuggestion[] = [];
      holder.querySelectorAll('span[data-mention="true"]').forEach((el) => {
        const he = el as HTMLElement;
        const email = he.getAttribute('data-email') || '';
        const userId = he.getAttribute('data-user-id') || undefined;
        const imageId = he.getAttribute('data-image-id') || undefined;
        // Remove leading @ symbols (handle both @ and @@ cases) and clean up the display
        // First remove invisible characters, then remove leading @, then trim
        let display = (he.textContent || '')
          .replace(/[\u200B-\u200D\uFEFF]/g, '') // Remove zero-width and BOM characters
          .replace(/^@+/, '') // Remove leading @ symbols
          .trim(); // Remove whitespace
        // Fallback to email if display is empty
        if (!display && email) display = email;
        // accept when userId or email is present
        if (userId || email) out.push({ email, display, userId, imageId });
      });
      return dedupeMentions(out);
    };

    const recomputeSelectedMentions = (html: string) => {
      setSelectedMentions(parseMentionsFromHtml(html));
    };

    const recomputeReplyMentions = (messageId: string, html: string) => {
      setReplyMentions((prev) => ({
        ...prev,
        [messageId]: parseMentionsFromHtml(html),
      }));
    };

    const recomputeEditMentions = (html: string) => {
      setEditMentions(parseMentionsFromHtml(html));
    };

    // ✅ Correct organizationId (robust fallbacks)
    const organizationId = useSelector((state: RootState) => state.userProfile?.userSphere?.properties?.sphereId);

    // unified Graph search: POST /search with type filter center_user
    const searchUsers = React.useCallback(
      async (term: string): Promise<SearchUserResult[]> => {
        if (!organizationId) return [];
        const query = term?.trim();
        if (!query) return [];

        const body = {
          searchTerm: query,
          filters: [{ property: 'type', value: 'center_user' }],
          limit: 10,
          page: 1,
        };

        try {
          const res = await graphClient.post('/search', body); // or full URL if your client isn't prefixed
          const data = res?.data ?? res;

          const spheres = Array.isArray(data?.spheres) ? data.spheres : [];
          return spheres
            .map((s: any) => ({
              id: s.id,
              type: s.properties?.type ?? s.type,
              name: s.properties?.name ?? s.label ?? '',
              email: s.properties?.email ?? '', // often empty
              imageId: s.properties?.imageId ?? s.imageId ?? '',
            }))
            .filter((u: { id: any; name: any; email: any }) => u.id && (u.name || u.email));
        } catch (e) {
          console.error('user search failed', e);
          return [];
        }
      },
      [graphClient, organizationId]
    );

    // caret token extraction
    function extractMentionToken(quill: any) {
      const sel = quill.getSelection();
      if (!sel) return null;
      const textUpTo = quill.getText(0, sel.index);
      // allow start-of-line, whitespace, or common punctuation before @
      const m = /(?:^|[\s([{.,:;!/?'"-])@([^\s]*)$/.exec(textUpTo);
      if (!m) return null;
      const fragment = m[1];
      const tokenLen = fragment.length + 1; // '@' + fragment
      const start = sel.index - tokenLen;
      const bounds = quill.getBounds(start, tokenLen); // relative to quill.root
      return { fragment, start, tokenLen, bounds };
    }

    function closeMention() {
      setMentionOpen(false);
      setMentionResults([]);
      setMentionLoading(false);
      setMentionQuery('');
      setMentionAnchor(null);
      setActiveIndex(0);
    }

    function openMention(quill: any, messageId?: string, isEdit?: boolean) {
      const tok = extractMentionToken(quill);
      if (!tok) {
        if (mentionOpen) closeMention();
        return;
      }

      const frag = tok.fragment.trim();
      setMentionOpen(true);
      setMentionQuery(tok.fragment);
      setActiveEditorMessageId(messageId || (isEdit ? 'edit' : null)); // Track which editor is active

      // Translate Quill-root-relative coords → wrapper-relative coords
      const rootRect = (quill.root as HTMLElement).getBoundingClientRect();
      let wrapRect;

      if (isEdit && editWrapperRef.current) {
        // Edit editor - use edit wrapper ref
        wrapRect = editWrapperRef.current.getBoundingClientRect();
      } else if (messageId && replyWrapperRefs.current[messageId]) {
        // Reply editor - use reply wrapper ref
        wrapRect = replyWrapperRefs.current[messageId]?.getBoundingClientRect();
      } else {
        // Main composer - use main wrapper ref
        wrapRect = editorWrapperRef.current?.getBoundingClientRect();
      }

      const left = tok.bounds.left + (rootRect.left - (wrapRect?.left ?? 0));
      const top = tok.bounds.top + tok.bounds.height + (rootRect.top - (wrapRect?.top ?? 0));

      setMentionAnchor({ left, top });

      if (lookupTimer.current) globalThis.clearTimeout(lookupTimer.current as any);
      if (frag.length < MIN_CHARS) {
        setMentionResults([]);
        setMentionLoading(false);
        return;
      }

      lookupTimer.current = globalThis.setTimeout(async () => {
        const seq = ++lookupSeq.current;
        setMentionLoading(true);
        const results = await searchUsers(tok.fragment);
        if (seq === lookupSeq.current) {
          setMentionResults(results);
          setActiveIndex(0);
          setMentionLoading(false);
        }
      }, 250) as unknown as number;
    }

    // insert mention span into quill + sync chips
    function insertMention(choice?: MentionSuggestion, messageId?: string, isEdit?: boolean) {
      const quill = getQuillEditor(isEdit, messageId);
      if (!quill || !choice) return;

      const tok = extractMentionToken(quill);
      if (!tok) return;

      const email = choice.email?.trim();
      const hasEmail = !!email;
      const hasUserId = !!choice.userId;
      if (!hasEmail && !hasUserId) return; // need at least one anchor

      const display = (choice.display || email || 'User').trim();
      const value = { email: email || '', display, userId: choice.userId, imageId: choice.imageId };

      insertMentionContent(quill, tok, value, display);

      const htmlNow = quill.root.innerHTML;
      updateContentAfterMention(htmlNow, isEdit, messageId);
      closeMention();
    }

    function insertMentionFromUser(u: SearchUserResult, messageId?: string, isEdit?: boolean) {
      const suggestion: MentionSuggestion = {
        email: (u.email || '').trim(), // may be empty
        display: (u.name || u.email || 'User').trim(),
        userId: u.id, // ✅ always given
        imageId: u.imageId,
      };
      insertMention(suggestion, messageId, isEdit);
    }

    function onPickFromPopover(item: MentionPopoverPick, messageId?: string, isEdit?: boolean) {
      if ('custom' in item) {
        insertMention({ email: item.email, display: item.display }, messageId, isEdit);
        return;
      }
      insertMentionFromUser(item, messageId, isEdit);
    }

    // remove a chip -> remove the corresponding mention(s) from the editor
    function removeMentionChip(idOrEmail: string) {
      const quill: any = composerRef.current?.getQuill();
      if (!quill) return;

      const root: HTMLElement = quill.root;

      // Match by user-id first; if not found, fall back to email
      let nodes = root.querySelectorAll(`span[data-mention="true"][data-user-id="${CSS.escape(idOrEmail)}"]`);
      if (!nodes.length) {
        nodes = root.querySelectorAll(`span[data-mention="true"][data-email="${CSS.escape(idOrEmail)}"]`);
      }

      Array.from(nodes).forEach((el: Element) => {
        const blot: any = QuillRef?.find?.(el);
        if (!blot) {
          // Fallback: no blot reference (e.g., tests) → remove DOM node text
          (el as HTMLElement).remove();
          return;
        }
        const index = blot.offset(quill.scroll);
        const isEmbed =
          (blot.statics && blot.statics.blotName === 'mention') ||
          (blot.statics && blot.statics.tagName === 'SPAN' && (el as HTMLElement).classList.contains('mention'));

        if (isEmbed && blot.length && blot.length() === 1) {
          quill.deleteText(index, 1, 'user');
          if (quill.getText(index, 1) === ' ') quill.deleteText(index, 1, 'user');
        } else {
          const labelLen = ((el as HTMLElement).textContent || '').length;
          if (labelLen > 0) {
            quill.deleteText(index, labelLen, 'user');
            if (quill.getText(index, 1) === ' ') quill.deleteText(index, 1, 'user');
          } else {
            (el as HTMLElement).remove();
          }
        }
      });

      const htmlNow = root.innerHTML;
      setComposer(htmlNow);
      recomputeSelectedMentions(htmlNow);
      quill.focus();
    }

    // remove a chip from reply editor -> remove the corresponding mention(s) from the reply editor
    function removeReplyMentionChip(messageId: string, idOrEmail: string) {
      const quill: any = replyEditorRefs.current[messageId]?.getQuill();
      if (!quill) return;

      const root: HTMLElement = quill.root;

      // Match by user-id first; if not found, fall back to email
      let nodes = root.querySelectorAll(`span[data-mention="true"][data-user-id="${CSS.escape(idOrEmail)}"]`);
      if (!nodes.length) {
        nodes = root.querySelectorAll(`span[data-mention="true"][data-email="${CSS.escape(idOrEmail)}"]`);
      }

      Array.from(nodes).forEach((el: Element) => {
        const blot: any = QuillRef?.find?.(el);
        if (!blot) {
          // Fallback: no blot reference (e.g., tests) → remove DOM node text
          (el as HTMLElement).remove();
          return;
        }
        const index = blot.offset(quill.scroll);
        const isEmbed =
          (blot.statics && blot.statics.blotName === 'mention') ||
          (blot.statics && blot.statics.tagName === 'SPAN' && (el as HTMLElement).classList.contains('mention'));

        if (isEmbed && blot.length && blot.length() === 1) {
          quill.deleteText(index, 1, 'user');
          if (quill.getText(index, 1) === ' ') quill.deleteText(index, 1, 'user');
        } else {
          const labelLen = ((el as HTMLElement).textContent || '').length;
          if (labelLen > 0) {
            quill.deleteText(index, labelLen, 'user');
            if (quill.getText(index, 1) === ' ') quill.deleteText(index, 1, 'user');
          } else {
            (el as HTMLElement).remove();
          }
        }
      });

      const htmlNow = root.innerHTML;
      setReplyDrafts((d) => ({ ...d, [messageId]: htmlNow }));
      recomputeReplyMentions(messageId, htmlNow);
      quill.focus();
    }

    // remove a chip from edit editor -> remove the corresponding mention(s) from the edit editor
    function removeEditMentionChip(idOrEmail: string) {
      const quill: any = editEditorRef.current?.getQuill();
      if (!quill) return;

      const root: HTMLElement = quill.root;

      // Match by user-id first; if not found, fall back to email
      let nodes = root.querySelectorAll(`span[data-mention="true"][data-user-id="${CSS.escape(idOrEmail)}"]`);
      if (!nodes.length) {
        nodes = root.querySelectorAll(`span[data-mention="true"][data-email="${CSS.escape(idOrEmail)}"]`);
      }

      Array.from(nodes).forEach((el: Element) => {
        const blot: any = QuillRef?.find?.(el);
        if (!blot) {
          // Fallback: no blot reference (e.g., tests) → remove DOM node text
          (el as HTMLElement).remove();
          return;
        }
        const index = blot.offset(quill.scroll);
        const isEmbed =
          (blot.statics && blot.statics.blotName === 'mention') ||
          (blot.statics && blot.statics.tagName === 'SPAN' && (el as HTMLElement).classList.contains('mention'));

        if (isEmbed && blot.length && blot.length() === 1) {
          quill.deleteText(index, 1, 'user');
          if (quill.getText(index, 1) === ' ') quill.deleteText(index, 1, 'user');
        } else {
          const labelLen = ((el as HTMLElement).textContent || '').length;
          if (labelLen > 0) {
            quill.deleteText(index, labelLen, 'user');
            if (quill.getText(index, 1) === ' ') quill.deleteText(index, 1, 'user');
          } else {
            (el as HTMLElement).remove();
          }
        }
      });

      const htmlNow = root.innerHTML;
      setEditDraft(htmlNow);
      recomputeEditMentions(htmlNow);
      quill.focus();
    }

    // keyboard while popover open (↑/↓/Enter/Escape)
    useEffect(() => {
      const quill = composerRef.current?.getQuill();
      if (!quill) return;

      const onKey = (e: KeyboardEvent) => {
        if (!mentionOpen) return;

        if (e.key === 'ArrowDown') {
          e.preventDefault();
          setActiveIndex((i) => (mentionResults.length ? (i + 1) % mentionResults.length : 0));
          return;
        }
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          setActiveIndex((i) => (mentionResults.length ? (i - 1 + mentionResults.length) % mentionResults.length : 0));
          return;
        }
        if (e.key === 'Enter') {
          e.preventDefault();
          if (mentionResults.length) {
            onPickFromPopover(mentionResults[Math.max(0, Math.min(activeIndex, mentionResults.length - 1))]);
          } else if (isValidEmail(mentionQuery)) {
            onPickFromPopover({ custom: true, email: mentionQuery.trim(), display: mentionQuery.trim() });
          } else {
            closeMention();
          }
          return;
        }
        if (e.key === 'Escape') {
          e.preventDefault();
          closeMention();
        }
      };

      quill.root.addEventListener('keydown', onKey as any);
      return () => quill.root.removeEventListener('keydown', onKey as any);
    }, [mentionOpen, mentionResults, activeIndex, mentionQuery]);

    // keep popover anchored to caret & recompute when user types/selects
    useEffect(() => {
      const quill = composerRef.current?.getQuill();
      if (!quill) return;

      const onText = () => {
        openMention(quill);
        recomputeSelectedMentions(quill.root.innerHTML);
      };
      const onSel = (range: any) => {
        if (!range) closeMention();
        else openMention(quill);
      };

      quill.on('text-change', onText);
      quill.on('selection-change', onSel);
      return () => {
        quill.off('text-change', onText);
        quill.off('selection-change', onSel);
      };
    }, []);

    // ──────────────────────────────────────────────────────────────────────────────
    // Render

    const getMessageTextColor = (level: number) => {
      return '#000';
    };

    const handleDeleteMessage = async (e: React.MouseEvent, messageId: string) => {
      e.preventDefault();
      e.stopPropagation();
      try {
        await deleteMessage(messageId);
      } catch (err) {
        console.error('delete failed', err);
      }
    };

    const handleReplyClick = (messageId: string) => {
      setReplyTo(messageId);

      // Use setTimeout to ensure the editor is rendered before trying to focus and scroll
      setTimeout(() => {
        const editor = replyEditorRefs.current[messageId];
        if (editor) {
          // Focus the editor
          const quill = editor.getQuill();
          if (quill) {
            quill.focus();
          }

          // Scroll to the editor
          const editorElement = editor.getElement();
          if (editorElement) {
            editorElement.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
            });
          }
        }
      }, 100);
    };

    const [hoveredMessageId, setHoveredMessageId] = useState<string | null>(null);
    const messageCounterRef = useRef(0);

    // Helper function to handle reply draft changes - extracted to reduce nesting
    const handleReplyDraftChange = (messageId: string, value: string) => {
      setReplyDrafts((d) => ({ ...d, [messageId]: value }));
    };

    const renderMessages = (parentId: string | null = null, level = 0): React.ReactNode => {
      const list = childrenMap[parentId ?? '__root__'] ?? [];

      // Reset counter for root messages
      if (parentId === null || parentId === '__root__') {
        messageCounterRef.current = 0;
      }

      return list.map((msg, index) => {
        const currentGlobalIndex = messageCounterRef.current;
        messageCounterRef.current++;
        const hasReplies = (childrenMap[msg.message_id]?.length || 0) > 0;

        return (
          <article
            key={msg.message_id}
            data-message-id={msg.message_id}
            className={`sphere-chat-message-container level-${level}`}
            aria-label={`Message from ${msg.author_id} at ${msg.created_at}`}
          >
            <Card
              className="sphere-chat-card"
              style={{
                background: getBgColor(level, currentGlobalIndex % 2 === 0),
                boxShadow: getBoxShadow(level, currentGlobalIndex % 2 === 0, hasReplies),
                color: getMessageTextColor(level),
              }}
              {...getMessageHoverProps(msg.message_id)}
              onTouchStart={() => !viewMode && handleTouchStart(msg.message_id)}
              onTouchEnd={handleTouchEnd}
              onTouchCancel={handleTouchEnd}
            >
              {editingMessageId === msg.message_id ? (
                <div className="sphere-chat-editor-wrapper" ref={editWrapperRef} style={UI_CONSTANTS.EDITOR_WRAPPER_STYLE}>
                  {isMobile ? (
                    <InputTextarea
                      value={editDraft}
                      onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setEditDraft(e.target.value)}
                      autoResize
                      rows={3}
                      placeholder="Edit your message..."
                      disabled={!effectiveCanEdit}
                      className="sphere-chat-input-textarea"
                      style={{ minHeight: 80, width: '100%' }}
                    />
                  ) : (
                    <>
                      <Editor
                        ref={editEditorRef}
                        value={editDraft}
                        onTextChange={(e) => {
                          const html = e.htmlValue || '';
                          setEditDraft(html);
                          // Handle mention popover for edit editor
                          const quill = editEditorRef.current?.getQuill();
                          setupMentionForEditor(quill, undefined, true);
                          // Update mentions for edit
                          recomputeEditMentions(html);
                        }}
                        style={{ minHeight: 80, width: '100%' }}
                        placeholder="Edit your message..."
                        className="sphere-chat-rich-editor"
                        readOnly={!effectiveCanEdit}
                      />

                      {/* Mentions popover for edit editor */}
                      <MentionPopover
                        open={mentionOpen && activeEditorMessageId === 'edit'}
                        anchor={mentionAnchor}
                        loading={mentionLoading}
                        results={mentionResults}
                        activeIndex={activeIndex}
                        query={mentionQuery}
                        onHover={setActiveIndex}
                        onPick={(item) => onPickFromPopover(item, undefined, true)}
                      />

                      {/* Mention chips bar for edit editor */}
                      {editMentions.length > 0 && <MentionChipsBar mentions={editMentions} onRemove={removeEditMentionChip} />}
                    </>
                  )}

                  <div className="flex justify-content-end gap-1 py-1">
                    <Button
                      label="Save"
                      icon="pi pi-check"
                      className="Save-button "
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        handleEditSave(msg.message_id);
                      }}
                      disabled={!effectiveCanEdit || !editDraft.trim() || editDraft === '<p><br></p>'}
                    />
                    <Button
                      label="Cancel"
                      className="Close-button"
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        setEditingMessageId(null);
                        setEditDraft('');
                        setEditMentions([]);
                      }}
                    />
                  </div>
                </div>
              ) : (
                <div className="sphere-chat-message-content" dangerouslySetInnerHTML={{ __html: sanitize(msg.content) }} />
              )}

              {editingMessageId !== msg.message_id && (
                <div className="sphere-chat-message-header">
                <AuthorBadge
                  authorSphereId={msg.author_id}
                  createdAt={msg.created_at}
                  edited={msg.edited}
                  actions={
                    viewMode ? undefined : (
                      <div
                        className="sphere-chat-message-actions"
                        style={{
                          opacity: hoveredMessageId === msg.message_id ? 1 : 0,
                          transition: 'opacity 0.2s ease-in-out',
                        }}
                        onMouseEnter={() => setHoveredMessageId(msg.message_id)}
                        role="toolbar"
                        aria-label="Message actions"
                      >
                        <MessageActionButton
                          icon="pi pi-reply"
                          tooltip={effectiveCanPost ? 'Reply' : "You can't reply"}
                          ariaLabel="Reply"
                          onClick={(e) => stopEventPropagation(e, handleReplyAction(msg.message_id))}
                          disabled={!effectiveCanPost}
                        />

                        {perms.canEdit && (
                          <>
                            <MessageActionButton
                              icon="pi pi-pencil"
                              tooltip="Edit"
                              ariaLabel="Edit"
                              onClick={(e) => stopEventPropagation(e, handleEditAction(msg.message_id, msg.content))}
                              disabled={!effectiveCanEdit}
                            />
                            <MessageActionButton
                              type="button"
                              icon={isDeleting(msg.message_id) ? 'pi pi-spinner pi-spin' : 'pi pi-trash'}
                              tooltip="Delete"
                              ariaLabel="Delete"
                              onClick={(e) => handleDeleteMessage(e, msg.message_id)}
                              disabled={isDeleting(msg.message_id) || !effectiveCanEdit}
                            />
                          </>
                        )}
                      </div>
                    )
                  }
                />
                {!viewMode && (
                  <button
                    className="sphere-chat-message-menu-btn"
                    onClick={() => handleOpenMobileMenu(msg.message_id)}
                    aria-label="Message options"
                  >
                    <i className="pi pi-ellipsis-v" />
                  </button>
                )}
              </div>
              )}
            </Card>

            {/* Render child replies as separate messages below parent */}
            {childrenMap[msg.message_id]?.length ? <div className="sphere-chat-replies-inline">{renderMessages(msg.message_id, level + 1)}</div> : null}

            {/* Reply draft editor as separate message */}
            {replyTo === msg.message_id && (
              <Card
                className="sphere-chat-card sphere-chat-draft-reply"
                style={{
                  background: getBgColor(level + 1, messageCounterRef.current % 2 === 0),
                  boxShadow: getBoxShadow(level + 1, messageCounterRef.current % 2 === 0, false),
                  color: getMessageTextColor(level + 1),
                  marginTop: '8px',
                  marginLeft: '5px',
                  marginRight: '5px',
                  marginBottom: '8px',
                }}
              >
                <div
                  className="sphere-chat-editor-wrapper"
                  ref={(el) => {
                    replyWrapperRefs.current[msg.message_id] = el;
                  }}
                  style={UI_CONSTANTS.REPLY_EDITOR_STYLE}
                >
                  {isMobile ? (
                    <InputTextarea
                      value={replyDrafts[msg.message_id] ?? ''}
                      onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
                        handleReplyDraftChange(msg.message_id, e.target.value)
                      }
                      autoResize
                      rows={3}
                      placeholder="Type your reply..."
                      disabled={!effectiveCanPost}
                      className="sphere-chat-input-textarea"
                      style={{ minHeight: 80, width: '100%' }}
                    />
                  ) : (
                    <>
                      <Editor
                        ref={(el) => {
                          replyEditorRefs.current[msg.message_id] = el;
                        }}
                        value={replyDrafts[msg.message_id] ?? ''}
                        onTextChange={(e) => {
                          const html = e.htmlValue || '';
                          setReplyDrafts((d) => ({ ...d, [msg.message_id]: html }));
                          // Update mentions for this reply
                          recomputeReplyMentions(msg.message_id, html);
                          // Handle mention popover for reply editors
                          const quill = replyEditorRefs.current[msg.message_id]?.getQuill();
                          setupMentionForEditor(quill, msg.message_id);
                        }}
                        style={{ minHeight: 80, width: '100%' }}
                        placeholder="Type your reply..."
                        className="sphere-chat-rich-editor"
                        readOnly={!effectiveCanPost}
                      />

                      {/* Mentions popover for reply editor */}
                      <MentionPopover
                        open={mentionOpen}
                        anchor={mentionAnchor}
                        loading={mentionLoading}
                        results={mentionResults}
                        activeIndex={activeIndex}
                        query={mentionQuery}
                        onHover={setActiveIndex}
                        onPick={(item) => {
                          const isEditMode = activeEditorMessageId === 'edit';
                          const msgId = isEditMode ? undefined : activeEditorMessageId || undefined;
                          onPickFromPopover(item, msgId, isEditMode);
                        }}
                      />

                      {/* Mention chips bar for reply editor */}
                      {replyMentions[msg.message_id]?.length > 0 && (
                        <MentionChipsBar mentions={replyMentions[msg.message_id]} onRemove={(idOrEmail) => removeReplyMentionChip(msg.message_id, idOrEmail)} />
                      )}
                    </>
                  )}
                  <div className="flex justify-content-end gap-1 py-2">
                    <SendButton
                      content={replyDrafts[msg.message_id] || ''}
                      className="p-button-sm Save-button"
                      onSend={handleSend}
                      loading={sending}
                      canPost={effectiveCanPost}
                    />
                    <Button label="Cancel" className="Close-button p-button-sm" onClick={handleCancelReply(msg.message_id)} disabled={sending} />
                  </div>
                </div>
              </Card>
            )}
          </article>
        );
      });
    };

    return (
      <section
        className="sphere-chat-panel"
        style={{
          boxShadow: theme === 'dark' ? '0 2px 4px rgba(0,0,0,0.3)' : '0 2px 4px rgba(0,0,0,0.1)',
        }}
        aria-label="Chat discussion panel"
      >
        <SpherePropertiesPanel contextKey={contextKey} canParticipate={canParticipateInProps} viewMode={viewMode} />
        {collapsible ? (
          <button
            className="sphere-chat-header collapsible"
            onClick={() => setIsExpanded(!isExpanded)}
            aria-expanded={isExpanded}
            type="button"
            style={{
              width: '100%',
              background: 'var(--component-background-gradient)',
              borderBottom: isExpanded ? 'var(--primary-border)' : 'none',
              border: 'none',
              color: 'var(--primary-text-color)',
              margin: 0,
              padding: '12px 16px',
              cursor: 'pointer',
              transition: 'background-color 0.2s ease',
            }}
          >
            <div className="sphere-chat-title" style={{ color: 'var(--primary-text-color)' }}>
              <i className="pi pi-comments sphere-chat-header-icon" style={{ color: 'var(--primary-text-color)' }} /> Discussion
            </div>
            <i className={`pi pi-chevron-right sphere-chat-toggle-icon ${isExpanded ? 'expanded' : ''}`} style={{ color: 'var(--secondary-text-color)' }} />
          </button>
        ) : (
          <div
            className="sphere-chat-header"
            style={{
              background: 'var(--component-background-gradient)',
              borderBottom: 'var(--primary-border)',
              padding: '12px 16px',
            }}
          >
            <div className="sphere-chat-title" style={{ color: 'var(--primary-text-color)' }}>
              <i className="pi pi-comments sphere-chat-header-icon" style={{ color: 'var(--secondary-text-color)' }} /> Discussion
            </div>
          </div>
        )}
        {showThreadContext && <ThreadContext contextKey={contextKey} className="compact" />}
        {isExpanded && (
          <div ref={chatMainRef} className="sphere-chat-main">
            {(() => {
              let mainContent;
              if (loading) {
                mainContent = renderLoadingSkeletons(3);
              } else if (!perms.canView) {
                mainContent = <PrimeMessage severity="warn" text="You don't have permission to view this conversation." />;
              } else if (err) {
                mainContent = <PrimeMessage severity="error" text={err} className="sphere-chat-empty-error" />;
              } else if (messages.length) {
                mainContent = (
                  <>
                    {renderMessages()}
                    <div ref={bottomRef} />
                  </>
                );
              } else {
                mainContent = (
                  <div className="sphere-chat-empty-state">
                    <i className="pi pi-comments sphere-chat-empty-icon" />
                    <div className="sphere-chat-empty-text">
                      <strong>No messages yet.</strong>
                      <br />
                      Be the first to post!
                    </div>
                  </div>
                );
              }
              return mainContent;
            })()}
          </div>
        )}

        {/* Bottom composer when not replying and not in view mode - moved outside sphere-chat-main */}
        {isExpanded && replyTo == null && !viewMode && (
          <div
            className="sphere-chat-input-bar"
            style={{
              paddingTop: '16px',
            }}
          >
            {!showRich || isMobile ? (
              <InputTextarea
                value={composer}
                onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setComposer(e.target.value)}
                autoResize
                rows={1}
                placeholder={getInputPlaceholder()}
                onFocus={() => !isMobile && setShowRich(true)}
                onKeyDown={(e: KeyboardEvent<HTMLTextAreaElement>) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
                disabled={sending || !effectiveCanPost}
                className="sphere-chat-input-textarea"
              />
            ) : (
              <div className="sphere-chat-editor-wrapper" ref={editorWrapperRef} style={UI_CONSTANTS.MAIN_EDITOR_STYLE}>
                <Editor
                  ref={composerRef}
                  value={composer}
                  onTextChange={(e) => {
                    const html = e.htmlValue || '';
                    setComposer(html);
                    const q = composerRef.current?.getQuill();
                    if (q) setTimeout(() => openMention(q), 0); // ensure caret is fresh
                    recomputeSelectedMentions(html); // keep chipbar in sync
                  }}
                  onSelectionChange={(e) => {
                    // Hide rich editor when it loses focus (blur)
                    if (e.range === null) {
                      // Check if the click/focus is outside the editor area
                      setTimeout(() => {
                        const activeElement = document.activeElement;
                        const editorWrapper = editorWrapperRef.current;
                        const isClickOnSendButton = activeElement?.closest('.sphere-chat-send-btn');
                        const isClickInEditor = activeElement?.closest('.sphere-chat-editor-wrapper');

                        if (!isClickOnSendButton && !isClickInEditor && editorWrapper && !editorWrapper.contains(activeElement)) {
                          setShowRich(false);
                        }
                      }, 100);
                    }
                  }}
                  style={{ minHeight: 80, width: '100%' }}
                  placeholder={getInputPlaceholder()}
                  className="sphere-chat-rich-editor"
                  readOnly={!effectiveCanPost}
                />

                {/* Mentions popover (shared component) */}
                <MentionPopover
                  open={mentionOpen}
                  anchor={mentionAnchor}
                  loading={mentionLoading}
                  results={mentionResults}
                  activeIndex={activeIndex}
                  query={mentionQuery}
                  onHover={setActiveIndex}
                  onPick={onPickFromPopover}
                />

                {/* Mention chips bar (below editor) */}
                <MentionChipsBar mentions={selectedMentions} onRemove={removeMentionChip} />
                <SendButton content={composer} className="p-button-sm sphere-chat-send-btn" onSend={handleSend} loading={sending} canPost={effectiveCanPost} />
              </div>
            )}


          </div>
        )}

        {/* Mobile Actions Menu */}
        {mobileMenuOpen && (
          <>
            <button
              type="button"
              className="sphere-chat-mobile-menu-backdrop"
              onClick={handleCloseMobileMenu}
              onKeyDown={handleBackdropKeyDown}
              aria-label={t('chat.actions.closeMenu')}
            />
            <div className={`sphere-chat-mobile-actions-menu ${mobileMenuOpen ? 'active' : ''}`}>
              <div className="menu-handle" />

              <button
                className="menu-action-item reply"
                onClick={() => selectedMessageForMenu && handleMobileMenuAction('reply', selectedMessageForMenu)}
                disabled={!effectiveCanPost}
              >
                <i className="pi pi-reply" />
                <span>{t('chat.actions.reply')}</span>
              </button>

              {perms.canEdit && selectedMessageForMenu && (
                <>
                  <button
                    className="menu-action-item edit"
                    onClick={() => {
                      const msg = messages.find(m => m.message_id === selectedMessageForMenu);
                      if (msg) {
                        handleMobileMenuAction('edit', selectedMessageForMenu, msg.content);
                      }
                    }}
                    disabled={!effectiveCanEdit}
                  >
                    <i className="pi pi-pencil" />
                    <span>{t('chat.actions.edit')}</span>
                  </button>

                  <button
                    className="menu-action-item delete"
                    onClick={(e) => {
                      if (selectedMessageForMenu) {
                        handleCloseMobileMenu();
                        handleDeleteMessage(e, selectedMessageForMenu);
                      }
                    }}
                    disabled={
                      !effectiveCanEdit ||
                      !!(selectedMessageForMenu && isDeleting(selectedMessageForMenu))
                    }
                  >
                    <i className={selectedMessageForMenu && isDeleting(selectedMessageForMenu) ? 'pi pi-spinner pi-spin' : 'pi pi-trash'} />
                    <span>{t('chat.actions.delete')}</span>
                  </button>
                </>
              )}
            </div>
          </>
        )}
      </section>
    );
  }
);

export default SphereChatPanel;
